import React, { useEffect, useState } from 'react';
import { ExternalLink, PauseCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDay = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

function rows(s) {
  const { gate, fees, anchor } = s;
  return [
    { id: 'gate', label: 'Consent receipt checks', status: gate.status, detail: gate.status === OFF ? `Not accepting receipts. ${gate.receipts} receipts on record from before the pause, ${gate.executed} executed.` : `${gate.receipts} receipts, ${gate.executed} executed` },
    { id: 'fees', label: 'Paid gate fees', status: fees.status, detail: fees.status === 'operational' ? `${fees.gate_price} USDC per execute on ${fees.chain}, refusals free` : fees.detail },
    { id: 'anchor', label: 'Daily Base anchor', status: anchor.status, detail: anchor.latest_date ? `Latest anchor ${fmtDay(anchor.latest_date)}, ${anchor.leaf_count} receipts` : anchor.detail, link: anchor.explorer_url },
  ];
}

const OFF = 'not_in_service';

function StatusRow({ r, first }) {
  const ok = r.status === 'operational';
  const off = r.status === OFF;
  const label = off ? 'Not in service' : ok ? 'Operational' : 'Degraded';
  const tone = off ? 'text-gray-400' : ok ? 'text-green-400' : 'text-yellow-400';
  const dot = off ? 'bg-gray-500' : ok ? 'bg-green-400' : 'bg-yellow-400';
  return (
    <div className={`flex items-center justify-between gap-4 px-6 py-5 ${first ? '' : 'border-t border-white/5'}`} data-testid={`binding-status-row-${r.id}`}>
      <div className="flex items-center gap-4 min-w-0">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
        <div className="min-w-0">
          <div className="font-medium">{r.label}</div>
          <div className="text-xs text-gray-500" data-testid={`binding-status-detail-${r.id}`}>{r.detail}</div>
          {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1 mt-1" data-testid="binding-status-anchor-link">View on BaseScan <ExternalLink className="w-3 h-3" /></a>}
        </div>
      </div>
      <div className={`text-sm font-medium shrink-0 ${tone}`} data-testid={`binding-status-value-${r.id}`}>{label}</div>
    </div>
  );
}

export function BindingGateStatus() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const load = () => fetch(`${API_URL}/api/binding/status`, { cache: 'no-store' }).then((r) => r.json()).then((d) => { setS(d); setErr(false); }).catch(() => setErr(true));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-12" data-testid="binding-status-section">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-semibold text-white/90 uppercase tracking-[0.18em] inline-flex items-center gap-2"><PauseCircle className="w-4 h-4 text-gray-400" /> Binding Action Gate</h2>
        {s && <span className={`text-xs ${s.status === 'operational' ? 'text-green-400' : s.status === 'proposed' ? 'text-gray-400' : 'text-yellow-400'}`} data-testid="binding-status-overall">{s.status === 'operational' ? 'All operational' : s.status === 'proposed' ? 'Proposed design. Not in service.' : 'Partially degraded'}</span>}
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {err && <div className="px-6 py-5 text-sm text-red-400" data-testid="binding-status-error">Status unavailable.</div>}
        {!s && !err && <div className="px-6 py-5 text-sm text-gray-500">Checking...</div>}
        {s && rows(s).map((r, i) => <StatusRow key={r.id} r={r} first={i === 0} />)}
      </div>
    </div>
  );
}
